/**
 * @sazeo/sdk — Time Tracking hooks
 *
 * TanStack Query wrappers for the time-tracking addon. All calls route through
 * the Edge Function at `/addons/time-tracking/*` guarded by
 * requireAddonEnabled() and enforced by RLS via the caller's JWT.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SupabaseClientLike } from './people';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface TimeEntry {
  id: string;
  tenant_id: string;
  user_id: string;
  project_id: string | null;
  description: string | null;
  started_at: string;
  ended_at: string | null;
  duration_minutes: number | null;
  billable: boolean;
  created_at: string;
  updated_at: string;
}

export interface TimeEntryCreateInput {
  description?: string | null;
  project_id?: string | null;
  started_at: string;
  ended_at?: string | null;
  billable?: boolean;
}

export interface TimeEntryUpdateInput extends Partial<TimeEntryCreateInput> {}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const timeEntryKeys = {
  all: ['time-entries'] as const,
  list: (tenantId: string) => [...timeEntryKeys.all, 'list', tenantId] as const,
  report: (tenantId: string, month: string) =>
    [...timeEntryKeys.all, 'report', tenantId, month] as const,
} as const;

export interface TimeReportRow {
  user_id: string;
  project_id: string | null;
  month_start: string;
  entry_count: number;
  total_hours: number;
  billable_hours: number;
  user_name?: string | null;
  project_name?: string | null;
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

async function getAuthHeaders(supabase: SupabaseClientLike): Promise<Record<string, string>> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  return token ? { authorization: `Bearer ${token}` } : {};
}

async function apiFetch<T>(
  supabase: SupabaseClientLike,
  path: string,
  init?: RequestInit,
): Promise<T> {
  const headers = await getAuthHeaders(supabase);
  const res = await fetch(`/functions/v1/${path}`, {
    ...init,
    headers: { 'content-type': 'application/json', ...headers, ...init?.headers },
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({ error: { message: res.statusText } }))) as {
      error?: { message?: string };
    };
    throw new Error(body.error?.message ?? res.statusText);
  }
  if (res.status === 204) return null as T;
  return res.json() as Promise<T>;
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

export function useTimeEntries(supabase: SupabaseClientLike, tenantId: string) {
  return useQuery({
    queryKey: timeEntryKeys.list(tenantId),
    queryFn: () => apiFetch<TimeEntry[]>(supabase, 'addons/time-tracking/entries'),
    enabled: Boolean(tenantId),
  });
}

export function useCreateTimeEntry(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: TimeEntryCreateInput) =>
      apiFetch<TimeEntry>(supabase, 'addons/time-tracking/entries', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: timeEntryKeys.list(tenantId) });
    },
  });
}

export function useUpdateTimeEntry(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: TimeEntryUpdateInput }) =>
      apiFetch<TimeEntry>(supabase, `addons/time-tracking/entries/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: timeEntryKeys.list(tenantId) });
    },
  });
}

export function useDeleteTimeEntry(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<void>(supabase, `addons/time-tracking/entries/${id}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: timeEntryKeys.list(tenantId) });
    },
  });
}

export function useTimeReport(
  supabase: SupabaseClientLike,
  tenantId: string,
  month?: string,
  userId?: string,
  projectId?: string,
  enabled = true,
) {
  const monthParam = month || new Date().toISOString().slice(0, 7);
  const params = new URLSearchParams({ month: monthParam });
  if (userId) params.set('user_id', userId);
  if (projectId) params.set('project_id', projectId);

  return useQuery({
    queryKey: timeEntryKeys.report(tenantId, monthParam),
    queryFn: () =>
      apiFetch<TimeReportRow[]>(supabase, `addons/time-tracking/report?${params.toString()}`),
    enabled: Boolean(tenantId) && enabled,
  });
}

export function useExportTimeReport(supabase: SupabaseClientLike) {
  return useMutation({
    mutationFn: async (month: string) => {
      const headers = await getAuthHeaders(supabase);
      const res = await fetch(
        `/functions/v1/addons/time-tracking/report/export?month=${month}&format=csv`,
        {
          headers,
        },
      );
      if (!res.ok) throw new Error(`Export failed: ${res.statusText}`);
      return res.blob();
    },
  });
}
